import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardBody,
  HStack,
  VStack,
  Text,
  Heading,
  Spinner,
  Center,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import proxy from "../proxy";

const GOAL = 500;
const ACCENT = "#48BB78";
const ACCENT_DIM = "rgba(72, 187, 120, 0.45)";

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const MonthlyDividendsChart = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const chartHeight = useBreakpointValue({ base: 200, sm: 240, md: 280 });

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${proxy}server_mts/GetMonthlyDividends.php`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setError(null);
      })
      .catch(() => !cancelled && setError("Unable to load dividend history."))
      .finally(() => !cancelled && setIsLoading(false));
    return () => { cancelled = true; };
  }, []);

  const series = useMemo(() => data?.series ?? [], [data]);

  const stats = useMemo(() => {
    if (series.length === 0) return null;
    const latest = series[series.length - 1];
    const best = series.reduce((a, b) => (b.net > a.net ? b : a));
    // Trailing 12 months received (net)
    const trailing12 = series.slice(-12).reduce((s, r) => s + r.net, 0);
    return {
      latest,
      best,
      trailing12,
      pctToGoal: (latest.net / GOAL) * 100,
    };
  }, [series]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const p = payload[0].payload;
    return (
      <Box
        bg="gray.800"
        p={3}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="teal.500"
        boxShadow="0 0 20px rgba(56, 178, 172, 0.3)"
      >
        <Text fontSize="sm" fontWeight="bold" color="white" mb={1}>
          {p.label}
        </Text>
        <Text fontSize="md" fontWeight="bold" color="green.300">
          {fmtMoney(p.net)} <Text as="span" fontSize="2xs" color="gray.500">net</Text>
        </Text>
        <Text fontSize="xs" color="gray.400">
          {fmtMoney(p.gross)} gross · {p.count} payment{p.count === 1 ? "" : "s"}
        </Text>
      </Box>
    );
  };

  return (
    <Card
      bg="rgba(45, 55, 72, 0.7)"
      borderWidth="1px"
      borderColor="teal.700"
      backdropFilter="blur(10px)"
      boxShadow="0 0 30px rgba(56, 178, 172, 0.15)"
      maxW="900px"
      mx="auto"
    >
      <CardBody p={{ base: 3, md: 4 }}>
        <VStack spacing={{ base: 3, md: 4 }} align="stretch">

          {/* Header */}
          <VStack spacing={0} align="flex-start">
            <Heading size="sm" color="teal.300">
              💵 Monthly Dividends Received
            </Heading>
            <Text fontSize="xs" color="gray.500">
              Net dividends paid each month, on the road to $500/month
            </Text>
          </VStack>

          {isLoading ? (
            <Center h={chartHeight}>
              <Spinner size="md" color="teal.300" thickness="3px" />
            </Center>
          ) : error ? (
            <Center h={chartHeight}>
              <VStack spacing={1}>
                <Text fontSize="2xl">📉</Text>
                <Text fontSize="sm" color="red.300">{error}</Text>
              </VStack>
            </Center>
          ) : series.length === 0 ? (
            <Center h={chartHeight}>
              <VStack spacing={1}>
                <Text fontSize="2xl">📊</Text>
                <Text fontSize="sm" color="gray.400">No dividends recorded yet.</Text>
              </VStack>
            </Center>
          ) : (
            <>
              {/* Stat cards */}
              {stats && (
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 2, md: 3 }}>
                  <Stat>
                    <StatLabel color="gray.400" fontSize="xs">Latest month</StatLabel>
                    <StatNumber color="green.300" fontSize={{ base: "lg", md: "xl" }}>
                      {fmtMoney(stats.latest.net)}
                    </StatNumber>
                    <StatHelpText fontSize="2xs" color="gray.500" mb={0}>
                      {stats.latest.label}
                    </StatHelpText>
                  </Stat>

                  <Stat>
                    <StatLabel color="gray.400" fontSize="xs">Best month</StatLabel>
                    <StatNumber color="purple.300" fontSize={{ base: "lg", md: "xl" }}>
                      {fmtMoney(stats.best.net)}
                    </StatNumber>
                    <StatHelpText fontSize="2xs" color="gray.500" mb={0}>
                      {stats.best.label}
                    </StatHelpText>
                  </Stat>

                  <Stat>
                    <StatLabel color="gray.400" fontSize="xs">Last 12 months</StatLabel>
                    <StatNumber color="teal.300" fontSize={{ base: "lg", md: "xl" }}>
                      {fmtMoney(stats.trailing12)}
                    </StatNumber>
                    <StatHelpText fontSize="2xs" color="gray.500" mb={0}>
                      net received
                    </StatHelpText>
                  </Stat>

                  <Stat>
                    <StatLabel color="gray.400" fontSize="xs">To $500 goal</StatLabel>
                    <StatNumber color="yellow.300" fontSize={{ base: "lg", md: "xl" }}>
                      {stats.pctToGoal.toFixed(1)}%
                    </StatNumber>
                    <StatHelpText fontSize="2xs" color="gray.500" mb={0}>
                      latest vs. target
                    </StatHelpText>
                  </Stat>
                </SimpleGrid>
              )}

              {/* Chart — auto-scaled to actual amounts */}
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={series} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: "#A0AEC0" }}
                    stroke="rgba(255,255,255,0.15)"
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#A0AEC0" }}
                    stroke="rgba(255,255,255,0.15)"
                    tickLine={false}
                    width={48}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(72,187,120,0.08)" }} />
                  <Bar dataKey="net" radius={[4, 4, 0, 0]} maxBarSize={48}>
                    {series.map((entry, i) => (
                      <Cell
                        key={entry.period}
                        fill={i === series.length - 1 ? ACCENT : ACCENT_DIM}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Footer */}
              <HStack justify="center" pt={1} flexWrap="wrap" gap={2}>
                <Badge colorScheme="green" fontSize="2xs" px={2} py={0.5}>
                  💰 {fmtMoney(data.totalNet)} net all-time
                </Badge>
                <Badge colorScheme="teal" fontSize="2xs" px={2} py={0.5}>
                  📊 {data.count} payments · {series.length} months
                </Badge>
              </HStack>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default MonthlyDividendsChart;
